import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { createSYS4010_R01, deleteSYS4010_R01, downloadExcel_SYS4010_R01, getSYS4010_R01AllCount, getSYS4010_R01List, getSYS4010_R01One, patchSYS4010_R01 } from "./Api_SYS4010_R01";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { defaultSYS4010_R01Dto, SYS4010_R01Dto } from "./Dto_SYS4010_R01";
import { defaultSYS4010_R01Filter, SYS4010_R01Filter, useSYS4010_R01Store } from "./Store_SYS4010_R01";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import useUserStore from "src/store/useUserStore";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	SYS4010_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS4010_R01Dto> | null>;	
	setMessage: (message: string) => void;
	
}

export const Content_SYS4010_R01 = (props: Props) => {

	const {
		gridRef,
		setMessage,
	} = props;

	const {
		SYS4010_R01Filter, setSYS4010_R01Filter,
		SYS4010_R01PastFilter, setSYS4010_R01PastFilter,
		SYS4010_R01List, setSYS4010_R01List,
		selectedSYS4010_R01, setSelectedSYS4010_R01,
		editSYS4010_R01, setEditSYS4010_R01,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useSYS4010_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { moneyCdList, paycollectTargetList, yesNoList, compCdList, fetchCompCdCode } = useCommonCodeStore();
	const { company, setUserInCompanyList } = useUserStore();

	useFetchActionbarAuth("SYS4010_R01");
	
	
	const handleExcelClick = useCallback(async () => {

		const response = await downloadExcel_SYS4010_R01(
			'',
			getParams(SYS4010_R01Filter),
			SYS4010_R01List.length
		);
		
		downloadSettingExcel({ responseData: response.data.data, fileName: '사업장 정보등록-excel.xlsx' });

	}, [SYS4010_R01Filter, SYS4010_R01List]);

	const handleCompNmChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSYS4010_R01Filter({...SYS4010_R01Filter, compNm: e.target.value});
	}

	const getParams = (filter: SYS4010_R01Filter) => {
		const params: any = {};
		if (filter.compNm) params.compNm = filter.compNm;
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSYS4010_R01AllCount(getParams(SYS4010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS4010_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);

		const responseData = await getSYS4010_R01List(
			'',
			getParams(SYS4010_R01Filter),
			SYS4010_R01List.length
		);
		let newList = [...responseData.data.data ]
		setSYS4010_R01List(newList);

		const processAllCount = await renewalAllCount();
		
		// 데이터가 있으면 첫 번째 행 선택
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedSYS4010_R01,
				() => true,// () => !data.find(value => value.compCd === editSYS4010_R01['compCd'])
			);
			setSYS4010_R01PastFilter(SYS4010_R01Filter);
		} else {
			showWarning('조회된 데이터가 없습니다.', 3000);
			handleCreateClick();
		}

		setLoadingSpinner(false);
		ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		setTimeout(() => setMessage(""), 5000);
		return newList;
	}, [SYS4010_R01Filter, SYS4010_R01List, gridRef, editSYS4010_R01, setSYS4010_R01List, setSelectedSYS4010_R01, setScrollTop]);

	const defaultSYS4010_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SYS4010_R01_ROW_VALIDATE, {
				moneyCdList,
				paycollectTargetList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultSYS4010_R01Dto };
		}

		return {
			...defaultSYS4010_R01Dto,
			moneyCd: moneyCdList[0].minorCd,
			collectTarget: paycollectTargetList[0].minorCd,
			paymentTarget: paycollectTargetList[0].minorCd,
			exceptionCollect: yesNoList[0].minorCd,
			exceptionPayment: yesNoList[0].minorCd,
			labelPrintAuto: 'N',
			inserted: true,
		};
	}, [defaultSYS4010_R01Dto, moneyCdList, paycollectTargetList, yesNoList, validateCommonCodeLists]);

	const fetchOne = useCallback(async () => {
		if (!selectedSYS4010_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getSYS4010_R01One(selectedSYS4010_R01.compCd);
			setEditSYS4010_R01({ ...response.data.data });
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);

	}, [SYS4010_R01Filter, setEditSYS4010_R01, selectedSYS4010_R01]);

	const initContainerData = useCallback(async () => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	const handleCreateClick = useCallback(async () => {
		setSelectedSYS4010_R01(undefined);
		setEditSYS4010_R01({ ...defaultSYS4010_R01Row(), })
	}, [SYS4010_R01Filter, selectedSYS4010_R01]);
	
	const handleDeleteClick = useCallback(async () => {
		if (!selectedSYS4010_R01) {
			showWarning('선택된 사업장이 없습니다.');
			return;
		}

		if (!window.confirm('정말로 삭제하시겠습니까?')) {
			return;
		}

		await deleteSYS4010_R01(selectedSYS4010_R01.compCd);
		showSuccess(`${selectedSYS4010_R01.compCd} 사업장 삭제가 완료되었습니다.`);
		refetchUpdateList('delete', defaultSYS4010_R01Row(), selectedSYS4010_R01, 'compCd', SYS4010_R01List, setSYS4010_R01List);
		handleCreateClick();
	}, [selectedSYS4010_R01]);

	const validateInsertData = (data: SYS4010_R01Dto) => {

		if (!validateString(data.compCd))			{ showWarning("사업장코드를 입력해주세요.");			return false; }
		if (!validateString(data.compNm))			{ showWarning("상호를 입력해주세요.");					return false; }
		if (!validateString(data.compNmFull))		{ showWarning("상호(Full)을 입력해주세요.");			return false; }
		if (!validateString(data.represent))		{ showWarning("대표자를 입력해주세요.");				return false; }
		if (!validateString(data.regNo))			{ showWarning("사업자번호를 입력해주세요.");			return false; }
		if (!validateString(data.telNo))			{ showWarning("전화번호를 입력해주세요.");				return false; }
		if (!validateString(data.faxNo))			{ showWarning("팩스번호를 입력해주세요.");				return false; }
		if (!validateString(data.taxMobile))		{ showWarning("휴대폰(세금계산서)을 입력해주세요.");	return false; }
		if (!validateString(data.taxEmail))			{ showWarning("E-mail(세금계산서)을 입력해주세요.");	return false; }
		if (!validateString(data.businessType))		{ showWarning("업태를 입력해주세요.");					return false; }
		if (!validateString(data.businessClass))	{ showWarning("종목을 입력해주세요.");					return false; }
		return true;
	}
	
	const handleSaveClick = useCallback(async () => {

		if(!validateInsertData(editSYS4010_R01)) return;

		let processName = '';
		let responseData: SYS4010_R01Dto = { ...defaultSYS4010_R01Row() };

		if (selectedSYS4010_R01) {
			processName = 'patch';
			const response = await patchSYS4010_R01(editSYS4010_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editSYS4010_R01.compCd} 사업장 수정이 완료되었습니다.`);
					setMessage(`${editSYS4010_R01.compCd} 사업장 수정이 완료되었습니다.`);
				}
			}else return;
		} else {
			processName = 'post';
			const response = await createSYS4010_R01(editSYS4010_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editSYS4010_R01.compCd} 사업장 등록이 완료되었습니다.`);
					setMessage(`${editSYS4010_R01.compCd} 사업장 등록이 완료되었습니다.`);
				}
			}else return;
		}

		setTimeout(() => {
			setMessage("");
		}, 5000)

		if(editSYS4010_R01) {
			refetchUpdateList(processName, responseData, editSYS4010_R01, 'compCd', SYS4010_R01List, setSYS4010_R01List);
		}
		const newCompCdList = await fetchCompCdCode();
		if (newCompCdList) {
			setUserInCompanyList(newCompCdList);
		}

	}, [editSYS4010_R01, compCdList, setUserInCompanyList]);

	useEffect(() => {
		if (selectedSYS4010_R01) {
			fetchOne();
		}
	}, [selectedSYS4010_R01]);

	return {
		handleExcelClick,
		handleCompNmChange,
		fetchData,
		defaultSYS4010_R01Row,
		fetchOne,
		initContainerData,
		handleCreateClick,
		handleSaveClick,
		handleDeleteClick,
	}

}
